import { ButtonV2 } from "#src/commonV2/Button";
import { FormFieldWrapper } from "#src/commonV2/form/helpers/FieldWrapper";
import { LoadingStateV2 } from "#src/commonV2/LoadingState";
import { SectionCard } from "#src/commonV2/SectionCard";
import { IPricingInfo } from "#src/services/payment/types";
import { planDurationToUserFriendlyString } from "#src/utils/apiHelper";
import {
  BookOpen01,
  CheckCircle,
  Sale03,
  ShoppingCart03,
  X,
} from "@untitled-ui/icons-react";
import { Fragment, ReactNode, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

interface IPaymentEnterInformationOrderSummaryProps {
  calculatedPricing: IPricingInfo;
  onApplyPromocode: (value: string) => Promise<boolean>;
  onRemovePromocode: (removeId: string) => void;
}

export const PaymentEnterInformationOrderSummary = ({
  calculatedPricing,
  onApplyPromocode,
  onRemovePromocode,
}: IPaymentEnterInformationOrderSummaryProps) => {
  const { t, i18n } = useTranslation("common", {
    keyPrefix: "v2.payment.enterInformation.orderSummary",
  });

  const locale = i18n.language;

  // --------------- STATES ---------------

  const [couponInput, setCouponInput] = useState<string>("");
  const [errorPromo, setErrorPromo] = useState<boolean>(false);

  // --------------- METHODS ---------------

  const LineOfPlanInfo = ({
    label,
    content,
  }: {
    label: ReactNode;
    content: ReactNode;
  }) => (
    <div className="flex justify-between gap-3 flex-wrap">
      <span className="text-gray-v2-600 text-sm">{label}</span>
      <span className="font-bold text-sm text-right">{content}</span>
    </div>
  );

  const onSubmitPromocode = () => {
    if (couponInput) {
      onApplyPromocode(couponInput).then((response) => {
        if (response) {
          setCouponInput("");
        } else {
          setErrorPromo(true);
        }
      });
    }
  };

  // --------------- RENDER ---------------

  return (
    <SectionCard
      header={
        <div className="flex items-center gap-4">
          <ShoppingCart03 className="h-6 w-6 min-w-6" />
          {t("title")}
        </div>
      }
      className="bg-brand-v2-400 border border-brand-v2-400 text-white h-fit"
    >
      {calculatedPricing === null ? (
        <LoadingStateV2 />
      ) : (
        <>
          <div className="p-4 pb-6 flex flex-col gap-4 rounded-t-xl bg-white text-gray-v2-900">
            {calculatedPricing.product_plans.map((plan) => (
              <Fragment key={plan.id}>
                <div className="flex flex-col gap-3">
                  <LineOfPlanInfo
                    label={t("package")}
                    content={
                      (locale === "vi"
                        ? plan.product.name.vi
                        : plan.product.name.en) +
                      " - " +
                      (locale === "vi" ? plan.name?.vi : plan.name?.en)
                    }
                  />
                  <LineOfPlanInfo
                    label={t("cycle")}
                    content={planDurationToUserFriendlyString(plan.duration)}
                  />
                  <LineOfPlanInfo
                    label={t("price")}
                    content={plan.price.toLocaleString() + " " + plan.currency}
                  />
                  {plan.discount ? (
                    <LineOfPlanInfo
                      label={
                        <div className="flex items-center gap-2 font-semibold text-gray-v2-900">
                          <Sale03 className="h-5 w-5 min-w-5 text-brand-v2-700" />
                          {plan.promo_code}
                        </div>
                      }
                      content={
                        <div className="flex items-center gap-4 text-sm font-bold">
                          {"-" +
                            Number(plan.discount.toFixed()).toLocaleString() +
                            " " +
                            plan.currency}
                          <button
                            className="flex items-center justify-center h-6 w-6 min-w-6 border bg-white border-error-v2-300 rounded-full"
                            onClick={() => {
                              onRemovePromocode(plan.id);
                            }}
                          >
                            <X className="h-5 w-5 min-w-5 text-error-v2-700" />
                          </button>
                        </div>
                      }
                    />
                  ) : null}
                </div>
                <hr className="border-t border-gray-v2-200" />
              </Fragment>
            ))}
            <FormFieldWrapper
              label={t("couponCode.label")}
              error={errorPromo ? t("invalidCode") : null}
            >
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  onSubmitPromocode();
                }}
              >
                <label className="px-3 py-2.5 flex items-center gap-2 flex-1 rounded-lg border border-dashed border-brand-v2-300">
                  <Sale03 className="h-5 w-5 min-w-5 text-brand-v2-700" />
                  <input
                    className="bg-transparent outline-none text-md text-gray-v2-900 placeholder:text-gray-v2-500 w-full"
                    placeholder={t("couponCode.label")}
                    value={couponInput}
                    onChange={(e) => {
                      setCouponInput(e.target.value.toUpperCase());
                      if (errorPromo) {
                        setErrorPromo(false);
                      }
                    }}
                  />
                </label>
                <ButtonV2 variant="secondary" type="submit">
                  <CheckCircle className="h-5 w-5 min-w-5" />
                  {t("button.apply")}
                </ButtonV2>
              </form>
            </FormFieldWrapper>
            <hr className="border-t border-gray-v2-200" />
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xl font-semibold text-gray-v2-900">
                  {t("total")}
                </span>
                <span className="text-display-xs font-semibold text-brand-v2-700 text-right">
                  {Number(
                    calculatedPricing.immediate_payment.toFixed()
                  ).toLocaleString()}{" "}
                  {calculatedPricing.currency}
                </span>
              </div>
              <div className="flex justify-center">
                <a className="flex items-center justify-center gap-1.5 no-underline text-md font-semibold text-brand-v2-700 hover:text-brand-v2-800 w-fit">
                  <BookOpen01 className="h-5 w-5 min-w-5" />
                  {t("learnMore")}
                </a>
              </div>
            </div>
          </div>
          <div className="p-4 rounded-b-xl text-md bg-gray-v2-100 text-gray-v2-600">
            <Trans
              t={t}
              i18nKey={"assureInfoIsCorrect"}
              components={{ b: <b className="text-gray-v2-900" /> }}
            />
          </div>
        </>
      )}
    </SectionCard>
  );
};
